import { useFocusEffect } from 'expo-router';
import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IconClose, Pressable, Text, createMqStyles, tokens } from '@fhs/ui';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';

interface PanelHeaderProps {
  onClose: () => void;
  /** Web only: links the panel's `aria-labelledby` to this heading. */
  titleNativeId?: string;
}

/**
 * The v2 locator supplies its own header instead of the navigation one, so the title and the
 * dismiss control stay inside the list panel on desktop.
 */
export const PanelHeader: React.VFC<PanelHeaderProps> = ({ onClose, titleNativeId }) => {
  const { formatMessage } = useIntl();
  const styles = usePanelHeaderMqStyles();
  const isDesktop = useIsDesktopBp();
  const { top } = useSafeAreaInsets();
  const closeButtonRef = useRef<View>(null);

  // This screen hides the navigation header, so it inherits the header's job of pulling focus
  // off the shell chrome when the route opens.
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') {
        return;
      }

      const frameId = requestAnimationFrame(() => {
        const element = closeButtonRef.current as unknown as { focus?: () => void } | null;
        element?.focus?.();
      });

      return () => cancelAnimationFrame(frameId);
    }, [])
  );

  return (
    // Apply the inset on the first render; native SafeAreaView can add it a frame later.
    <View style={[styles.safeArea, { paddingTop: top }]}>
      <View style={styles.container}>
        {/* Equal-width spacer so the title stays centred against the close control. */}
        <View style={styles.closeButton} />
        <Text.Heading type="two" nativeID={titleNativeId} style={styles.title}>
          Select Pickup Location
        </Text.Heading>
        <Pressable
          ref={closeButtonRef}
          testID="store-locator-close-button"
          accessibilityLabel={formatMessage({ id: 'close' })}
          dd-action-name="store locator close"
          onPress={onClose}
          borderRadius={999}
          style={styles.closeButton}
          hoveredStyle={styles.closeButtonHovered}
        >
          <IconClose size={isDesktop ? 28 : 24} color={tokens.colors.$houseDark} />
        </Pressable>
      </View>
    </View>
  );
};

const usePanelHeaderMqStyles = createMqStyles({
  safeArea: {
    $base: {
      backgroundColor: tokens.colors.$white,
    },
  },
  container: {
    $base: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 8,
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    $gteDesktop: {
      gap: 12,
      paddingHorizontal: 24,
      paddingVertical: 32,
    },
  },
  // The mobile app bar sets the title in body type, not the desktop heading size.
  title: {
    $base: {
      flex: 1,
      flexShrink: 1,
      textAlign: 'center',
    },
    $ltDesktop: {
      fontSize: 14,
      lineHeight: 20,
    },
  },
  closeButton: {
    $base: {
      alignItems: 'center',
      flexShrink: 0,
      height: 40,
      justifyContent: 'center',
      width: 40,
    },
  },
  closeButtonHovered: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
});
